import createLogger from "../../utils/logger.util";
import { Base } from "..";

import { PaymentProviderAccessorType } from "./types";

const logger = createLogger({ name: "Payment" });

class Payment extends Base {
	public static async updateBalance(data: Payment.UpdateBalance) {
		try {
			logger.info("[Payment] update balance ", data);
			const res = await this.request(
				(prpc) => prpc.theirsModel.payment.updateBalance(data),
				{ silent: false, error: true },
			);
			if (res?.error) {
				logger.error("[Payment] update balance error", { res });
				return true;
			}
			return true;
		} catch (err) {
			logger.error("[Payment] update balance error", err);
			return false;
		}
	}

	/**
	 * Открытые заказы для ссылки на оплату: по телефону (телефоны заказа и
	 * пассажиров) или по номеру заказа, только филиалы диспетчера.
	 */
	public static async findOrdersForPaymentLink(
		params: Payment.FindOrdersForPaymentLink,
	): Promise<Payment.PaymentLinkCandidate[]> {
		const res = await this.request(
			(prpc) => prpc.theirsModel.payment.findOrdersForPaymentLink(params),
			{ silent: false },
		);
		return res?.items ?? [];
	}

	/**
	 * Отправить клиенту ссылку на оплату заказа по SMS. Ошибка сервера
	 * пробрасывается: её `message` — код (`ORDER_PAYMENT_LINK_*`).
	 */
	public static async sendOrderPaymentLinkSms(
		params: Payment.SendOrderPaymentLinkSms,
	): Promise<Payment.SendOrderPaymentLinkSmsResult> {
		return this.request(
			(prpc) => prpc.theirsModel.payment.sendOrderPaymentLinkSms(params),
			{ silent: false },
		);
	}
}

declare namespace Payment {
	interface UpdateBalance {
		amount: number;
		description: string;
		paymentAccountId: number;
		providerAccessor: {
			id: number;
			type: PaymentProviderAccessorType;
		};
	}

	interface FindOrdersForPaymentLink {
		phone?: string;
		orderNumber?: number;
		limit?: number;
	}

	/** Почему по заказу нельзя отправить ссылку. */
	type PaymentLinkUnavailableReason =
		| "not_executing"
		| "no_executor"
		| "not_cash"
		| "already_paid"
		| "sms_link_disabled";

	interface PaymentLinkCandidate {
		id: number;
		orderNumber: number;
		status: string;
		executingStage: string | null;
		paymentType: string;
		taxiServiceId: number;
		companyId: number;
		createdAt: string;
		amount: number | null;
		currency: { code: string | null; symbol: string | null } | null;
		processedByTaximeter: boolean;
		executor: {
			id: number;
			callSign: string | null;
			name: string | null;
		} | null;
		qrPayment: {
			status: "pending" | "paid" | "failed" | "cancelled";
			url?: string;
			amount?: number;
		} | null;
		matchedBy: "orderPhone" | "passengerPhone" | "orderNumber" | "orderId";
		available: boolean;
		unavailableReason: PaymentLinkUnavailableReason | null;
	}

	interface SendOrderPaymentLinkSms {
		orderId: number;
		phone: string;
		smsTemplateId?: number;
		/** Отправить по текущей цене без подтверждения паузы таксометра. */
		force?: boolean;
	}

	interface SendOrderPaymentLinkSmsResult {
		success: boolean;
		url: string;
		invoiceId: string;
		amount: number;
		provider: string;
		reused: boolean;
		phone: string;
		sentAt: string;
	}
}

export default Payment;
